<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	//Stores
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Checkbox, Input, Text, Token } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { buttonOptions } from 'src/helpers/settings/settings_config_helpers';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device;

	let id = real_id(device.id),
		settings = readDeviceSettings(id),
		autoOff = settings?.ui?.brightness?.auto_off?.enable,
		byLux = settings?.ui?.brightness?.auto_off?.by_lux,
		cacheAutoOff = autoOff,
		cacheByLux = byLux,
		loading = false,
		timeout = settings?.ui?.brightness?.auto_off?.timeout || 0,
		cacheTimeout = timeout,
		selected =
			buttonOptions()?.find((opt) => opt.value === timeout)?.key ??
			(timeout === 0 ? 'Off' : undefined),
		customTimeout = !selected && timeout > 0 ? timeout : '';
	/**
	 id: 3method: "ui.setconfig"params: {config: {brightness: {auto_off: {enable: true, by_lux: false}}}}src: "d4b9ff35-46e5-44ec-af80-3076c849ee10"
	 */
	$: deviceFwVersion =
		(
			settings?.DeviceInfo?.ver ||
			settings?.sys?.device?.fw_id ||
			settings?.DeviceInfo?.fw_id
		)?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0] || '1.0.0';

	const sendRequest = async () => {
		const selectedOption = buttonOptions()?.find((opt) => opt.key === selected);
		const value = selectedOption ? selectedOption.value : Number(customTimeout);

		if (cacheTimeout === value && cacheAutoOff === autoOff && cacheByLux === byLux) return;

		loading = true;

		let params: Record<string, any>;

		if (compareFWVersions(deviceFwVersion, '<', '2.6.0')) {
			params = {
				id: device.channel,
				config: {
					brightness: { auto_off: { enable: autoOff, by_lux: byLux } },
					screen_off_when_idle: autoOff,
				},
			};
		} else {
			autoOff = value > 0;
			params = {
				id: device.channel,
				config: {
					brightness: { auto_off: { enable: autoOff, by_lux: byLux, timeout: value } },
					screen_off_when_idle: autoOff,
				},
			};
		}

		const res: SettingRequest = await gen2Request(device, `ui.SetConfig`, params);
		const response = await onSettingsResponse(res, device);
		if (response) {
			cacheTimeout = value;
			cacheAutoOff = autoOff;
			cacheByLux = byLux;
			settings.ui = {
				...settings.ui,
				...params.config,
			};
			devices_settings.update_devices_settings(id, settings);
		}
		loading = false;
	};

	const tokenClickHandler = (key: string) => {
		selected = key;
		customTimeout = '';
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: sendRequest, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if compareFWVersions(deviceFwVersion, '>=', '2.6.0')}
	<div class="d-flex justify-content-between">
		{#each buttonOptions() as option}
			<Token
				id="token-0d9p81s"
				size="m"
				title={option.title}
				on:click={() => tokenClickHandler(option.key)}
				selected={selected === option.key}
			/>
		{/each}
	</div>
	<Text styleType="body-small" class="mt-s mb-s">
		{$format('text_screen_off_timeout_description', {
			default: 'Choose from the predefined timeouts or set a custom timeout in seconds.',
		})}
	</Text>
	<Input
		aria-label={$format('input_screen_off_timeout', {
			default: 'Screen off timeout',
		})}
		type="number"
		icon="fas fa-clock"
		value={customTimeout}
		error={valueOutOfRange(customTimeout, 0, 60)
			? $format('min_max_error_description', {
					values: { min: 0, max: 60 },
					default: 'Value must be between {min} and {max}.',
				})
			: ''}
		on:input={(e) => {
			customTimeout = e.target['value'];
			selected = undefined;
		}}
		id="input-x7wmadsvf0"
	/>
{:else}
	<Checkbox
		checked={autoOff}
		size="m"
		value={autoOff}
		on:click={() => (autoOff = !autoOff)}
		id="checkbox-irrztkm"
		>{$format('option_auto_off', {
			default: 'Turn off screen when idle.',
		})}</Checkbox
	>
{/if}

{#if selected !== 'Off' || compareFWVersions(deviceFwVersion, '<', '2.6.0')}
	<Checkbox
		checked={byLux}
		size="m"
		class="mt-xs"
		value={byLux}
		on:click={() => (byLux = !byLux)}
		id="checkbox-dqheodf"
		>{$format('option_by_lux', {
			default: 'Only when dark.',
		})}</Checkbox
	>
{/if}

{#if !$showNewLayout}
	<Button class="mt-s" {loading} size="m" width="full" on:click={sendRequest}
		>{$format('button_save')}</Button
	>
{/if}
